import { createMark } from '@/components/editor/lib/createMark'
import type { EditorAttributes } from '@/components/editor/lib/types'
import type { Editor, Range } from '@tiptap/react'
import {
  AddToolForm,
  OPEN_TOOL_PLACEHOLDER_MODAL_EVENT,
} from '@/components/editor/components/AddToolForm/AddToolForm'
import {
  createTableMutation,
  toolsTableQuery,
  updateTableMutation,
} from '@/services/hooks/template_attributes'
import queryClient from '@/queryClient'
import QUERY_KEYS from '@/services/queries/tools/queryKeys'
import type { CommandOption } from './types'

const TOOLS_TABLE_TYPE = 'tools-table'
const TOOLS_TABLE_HEADER = { toolName: 'Tools' }
const MARK_COLOR = '#E5D5FF'

const refreshToolsQueries = async () => {
  /**
   * Tool membership for a document version is derived from tool references.
   * After adding references from slash command, refresh all active tools lists.
   */
  await queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.TOOLS],
    exact: false,
    refetchType: 'all',
  })
}

const isSameTool = (
  row: { toolName?: string; toolId?: string },
  tool: { toolName: string; toolId?: string },
) => {
  // Invariant: if both rows have a tool id, id is the source of truth.
  if (row.toolId && tool.toolId) {
    return row.toolId === tool.toolId
  }

  return row.toolName === tool.toolName
}

const upsertToolsRows = async ({
  documentPageId,
  rowsToAdd,
}: {
  documentPageId: string
  rowsToAdd: Array<{ toolName: string; toolId?: string }>
}) => {
  if (!rowsToAdd.length) return

  const tableTemplate = await toolsTableQuery(documentPageId)

  if (tableTemplate) {
    const existingRows = ((tableTemplate.template_values as any)?.rows ||
      []) as Array<{
      toolName?: string
      toolId?: string
    }>
    const nextRows = [...existingRows]
    let didAppendRow = false

    rowsToAdd.forEach((row) => {
      const alreadyExists = nextRows.some((existingRow) =>
        isSameTool(existingRow, row),
      )
      if (!alreadyExists) {
        nextRows.push(row)
        didAppendRow = true
      }
    })

    const existingColumns =
      (tableTemplate.template_values as any)?.columns || []
    const hasToolNameColumn = existingColumns.some((col: any) =>
      Object.prototype.hasOwnProperty.call(col, 'toolName'),
    )
    const nextColumns = hasToolNameColumn
      ? existingColumns
      : [...existingColumns, TOOLS_TABLE_HEADER]

    if (!didAppendRow && hasToolNameColumn) {
      // Avoid no-op writes when rows/columns are unchanged.
      return
    }

    await updateTableMutation().execute({
      templateAttributeId: tableTemplate.id as string,
      documentPageId,
      tableData: {
        type: TOOLS_TABLE_TYPE,
        rows: nextRows,
        columns: nextColumns,
      },
    })

    return
  }

  await createTableMutation().execute({
    documentPageId,
    tableData: {
      type: TOOLS_TABLE_TYPE,
      rows: rowsToAdd,
      columns: [TOOLS_TABLE_HEADER],
    },
  })
}

const insertToolsMarksIntoEditor = ({
  editor,
  range,
  tools,
}: {
  editor: Editor
  range: Range
  tools: Array<{ toolName: string; toolId?: string }>
}) => {
  const content = tools
    .map((tool) =>
      createMark({
        text: tool.toolName,
        color: MARK_COLOR,
        dataType: 'tool',
        dataAttributes: {
          'data-toolid': tool.toolId,
        },
      }),
    )
    .join(' ')

  editor.chain().focus().deleteRange(range).insertContent(content).run()
}

const command: CommandOption = {
  name: 'tools',
  path: '/tools:',
  multiSelect: false,
  alwaysShowEmptyResults: true,
  getItems: async ({ documentPageId, tools }) =>
    // Slash command options come from the tools endpoint response.
    tools
      .filter((tool) => Boolean(tool?.name))
      .map((tool) => ({
        key: `${documentPageId}-${tool.id}`,
        value: tool.name,
        toolId: tool.id,
      })),
  onSelect: async ({
    editor,
    range,
    items,
    templateAttributes,
    updateBomOnSelect,
    toast,
    query,
  }) => {
    const queryToolName = query.replace(/^\/?tools:/, '').trim()

    // Invariant: users can only insert tools that were explicitly selected.
    if (!items.length) {
      return
    }

    const editorAttributes = editor.options.editorProps
      .attributes as EditorAttributes

    const selectedTools = new Map<
      string,
      { toolName: string; toolId?: string }
    >()
    items.forEach((item) => {
      /**
       * Only canonical organization tools are selectable in slash command.
       * Items without toolId represent the no-results typed query pathway.
       */
      if (!item?.value || !item?.toolId) return
      selectedTools.set(item.value, {
        toolName: item.value,
        toolId: item.toolId,
      })
    })

    if (!selectedTools.size) {
      if (queryToolName && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(OPEN_TOOL_PLACEHOLDER_MODAL_EVENT, {
            detail: { query: queryToolName },
          }),
        )
      }
      return
    }

    /**
     * Behavior:
     * Editors opt into BOM/table mutation explicitly. Comments leave this off,
     * so tool slash commands only insert a visual pill there.
     */
    const customToolsRowsRaw = templateAttributes.find(
      (template) =>
        template.data_type === 'table' &&
        template.template_values.type === TOOLS_TABLE_TYPE,
    )?.template_values?.rows
    const customToolsRows: Array<{
      toolName?: string
      toolId?: string
    }> = Array.isArray(customToolsRowsRaw) ? customToolsRowsRaw : []

    const rowsToAdd = Array.from(selectedTools.values()).filter((tool) => {
      return !customToolsRows.some((row) => isSameTool(row, tool))
    })

    if (rowsToAdd.length && updateBomOnSelect) {
      try {
        await upsertToolsRows({
          documentPageId: editorAttributes.documentPageId as string,
          rowsToAdd,
        })
      } catch (error) {
        console.error(error)
        toast({
          variant: 'destructive',
          title: 'Failed to add selected tools to Tools BOM',
        })
      }
    }

    if (updateBomOnSelect) {
      try {
        await refreshToolsQueries()
      } catch (error) {
        console.error(error)
      }
    }

    insertToolsMarksIntoEditor({
      editor,
      range,
      tools: Array.from(selectedTools.values()),
    })
  },
  renderEmptyResults: ({
    query,
    editor,
    range,
    updateBomOnSelect,
    isSelected,
  }) => (
    <AddToolForm
      key="add-tool-form"
      query={query}
      updateBomOnSelect={updateBomOnSelect}
      isSelected={isSelected}
      onToolCreated={async (tool) => {
        const createdToolName = tool.name.trim()
        if (!tool.id || !createdToolName) {
          return
        }

        try {
          const editorAttributes = editor.options.editorProps
            .attributes as EditorAttributes
          if (updateBomOnSelect) {
            await upsertToolsRows({
              documentPageId: editorAttributes.documentPageId as string,
              rowsToAdd: [{ toolName: createdToolName, toolId: tool.id }],
            })
          }
          await refreshToolsQueries()
        } catch (error) {
          console.error(error)
        }

        insertToolsMarksIntoEditor({
          editor,
          range,
          tools: [{ toolName: createdToolName, toolId: tool.id }],
        })
      }}
    />
  ),
}

export default command
